import { useState } from 'react'
import { Plus } from 'lucide-react'
import { Button, CopyField, Notice } from '@/components/play'
import { useCameraState } from '@/components/workspace/WorkspaceMedia'
import { WorkspaceVideo } from '@/components/workspace/WorkspaceVideo'
import { WorkspaceInfo } from '@/components/workspace/WorkspaceInfo'
import { workspaceError } from '@/lib/workspace'
import type { StreamResource } from '@/types/events'

interface LiveShowPanelProps {
  stream?: StreamResource
  enabling: boolean
  featured: boolean
  featureBusy: boolean
  publicEnabled: boolean
  onEnable: () => Promise<StreamResource>
  onPreview: () => void
  onFeature: () => void | Promise<void>
  onSetup: () => void
  onVisibilityChange?: (visible: boolean) => Promise<void>
}

export function LiveShowPanel(props: LiveShowPanelProps) {
  const [error, setError] = useState('')

  async function enable() {
    if (props.enabling) return
    setError('')
    try {
      await props.onEnable()
    } catch (failure) {
      setError(workspaceError(failure, 'Your live show couldn’t be added. Try again.'))
    }
  }

  return (
    <section className="pw-live-show" aria-labelledby="live-show-heading">
      <div className="pw-live-show-heading">
        <h2 id="live-show-heading">Live show</h2>
        {!props.stream && (
          <div className="pw-live-show-heading-actions">
            <WorkspaceInfo label="About live show">
              An optional return for your finished OBS mix. Add live show to set it up.
            </WorkspaceInfo>
          </div>
        )}
      </div>
      {props.stream ? (
        <LiveShowMonitor {...props} stream={props.stream} />
      ) : (
        <div className="pw-live-show-intro">
          <Button variant="secondary" loading={props.enabling} onClick={() => void enable()}>
            <Plus />
            {props.enabling ? 'Adding live show…' : 'Add live show'}
          </Button>
          {error && (
            <Notice tone="error" alert>{error}</Notice>
          )}
        </div>
      )}
    </section>
  )
}

function LiveShowMonitor({
  stream,
  featured,
  featureBusy,
  publicEnabled,
  onPreview,
  onFeature,
  onSetup,
  onVisibilityChange,
}: LiveShowPanelProps & { stream: StreamResource }) {
  const state = useCameraState(stream)
  const publiclyVisible = stream.attributes.publicly_visible !== false
  const input = stream.attributes.connection_details?.external_ingress_url
  const rtmpServer = stream.attributes.connection_details?.rtmp_server_url
  return (
    <div className="pw-live-show-video">
      <WorkspaceVideo
        stream={stream}
        showName={false}
        featured={featured}
        featureBusy={featureBusy}
        canFeature={featured || (publicEnabled && publiclyVisible && state === 'live')}
        featureDisabledReason={!publicEnabled
          ? 'Turn on the public event page in event settings first'
          : !publiclyVisible
            ? 'Make this video public before featuring it'
            : 'Wait for video before featuring this show'}
        onPreview={onPreview}
        onFeature={onFeature}
        onSetup={onSetup}
        onVisibilityChange={onVisibilityChange}
        info="Your finished mix from OBS. Open OBS setup for the destination and import steps."
      />
      {(input || rtmpServer) && (
        <div className="pw-live-show-inputs">
          {input && (
            <CopyField
              label="SRT input"
              copyLabel="Copy live show SRT input"
              value={input}
              size="compact"
              labelAccessory={<WorkspaceInfo label="About the SRT input">Send your finished mix here from OBS or any SRT encoder.</WorkspaceInfo>}
            />
          )}
          {rtmpServer && (
            <>
              <CopyField
                label="RTMP server"
                copyLabel="Copy live show RTMP server"
                value={rtmpServer}
                size="compact"
                labelAccessory={<WorkspaceInfo label="About the RTMP input">For a studio that only sends RTMP: this server with the stream key below.</WorkspaceInfo>}
              />
              <CopyField label="Stream key" copyLabel="Copy live show stream key" value={stream.attributes.stream_key} size="compact" />
            </>
          )}
        </div>
      )}
    </div>
  )
}
